import LoadingOutlined from '@ant-design/icons/LoadingOutlined'
import { Button, List, Popover, Spin, Tag, Tooltip, Typography } from 'antd'
import {
  OntologyFieldDefinition,
  OntologyValueType,
} from 'features/dynamicOntology'
import { Brain } from 'lucide-react'
import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react'
import styled from 'styled-components'

import { getConfidenceColor } from './getConfidenceColor'
import { ExtractionResult, validFieldType } from './sageInferenceApi'
import { useSageInferenceSwr } from './useSageInferenceSwr'

const { Text } = Typography

type ExtractedValue = ExtractionResult['values'][number]

export type InferenceButtonProps = {
  fieldDef?: OntologyFieldDefinition
  setValue: Dispatch<SetStateAction<OntologyValueType>>
  documentId: string
}

const StyledBrain = styled(Brain)<{ $didFire: boolean }>`
  transition: scale 0.2s ease;
  color: ${({ theme, $didFire }) =>
    $didFire ? theme.colors.LimeGreen : theme.forecast.primary};
  width: 16px;
  height: 16px;
`

const FixedSizeButton = styled(Button)`
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
`

const RawInferenceButton: React.FC<InferenceButtonProps> = ({
  fieldDef,
  setValue,
  documentId,
}) => {
  const [doFire, setDoFire] = useState(false)
  const fieldType = validFieldType(fieldDef?.type)
  const canFire = !!fieldType

  const {
    data: extractionResults,
    isLoading,
    mutate,
  } = useSageInferenceSwr(
    fieldDef && fieldType && doFire
      ? {
          documentRolodexId: documentId,
          fieldName: fieldDef.name,
          fieldType: fieldType,
        }
      : null
  )

  const handleClick = useCallback(() => {
    if (!canFire) return
    if (doFire) {
      mutate()
      return
    }
    setDoFire(true)
  }, [canFire, doFire, mutate])

  const didFire = doFire && !isLoading

  const popoverContent = useMemo(() => {
    if (isLoading) {
      return (
        <StyledHelpTooltipContent>
          <Brain size={16} />
          <Text strong>Thinking...</Text>
        </StyledHelpTooltipContent>
      )
    }
    if (!didFire)
      return (
        <StyledHelpTooltipContent>
          <Brain size={16} />
          <Text strong>Click to have AI infer a value for this field</Text>
        </StyledHelpTooltipContent>
      )
    return extractionResults && extractionResults.values.length > 0 ? (
      <InferredValueList
        extractionResults={extractionResults}
        setValue={setValue}
      />
    ) : (
      <NoResultsSplash reasoning={extractionResults?.reasoning} />
    )
  }, [didFire, extractionResults, isLoading, setValue])

  return fieldType ? (
    <Popover content={popoverContent} trigger='hover' placement='topRight'>
      <FixedSizeButton onClick={handleClick} disabled={!canFire} type='dashed'>
        <Spin spinning={isLoading} indicator={<LoadingOutlined />}>
          <StyledBrain $didFire={didFire} />
        </Spin>
      </FixedSizeButton>
    </Popover>
  ) : null
}

export const InferenceButton = React.memo(RawInferenceButton)

const StyledHelpTooltipContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const NoResultsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
  color: #8c8c8c;
  text-align: center;
  max-width: min(500px, 90vw);
`

const NoResultsIcon = styled(Brain)`
  margin-bottom: 8px;
  opacity: 0.5;
`

const NoResultsTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  word-wrap: break-word;
  overflow-wrap: break-word;
  max-width: 100%;

  .ant-typography {
    word-wrap: break-word;
    overflow-wrap: break-word;
  }
`

const NoResultsSplash: React.FC<{ reasoning?: string }> = ({ reasoning }) => {
  return (
    <NoResultsContainer>
      <NoResultsIcon size={32} />
      <NoResultsTextContainer>
        <Text strong>Could not find a value for this field</Text>
        {reasoning && <Text>{reasoning}</Text>}
      </NoResultsTextContainer>
    </NoResultsContainer>
  )
}

const ItemContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-width: 0;

  > span:first-child {
    word-wrap: break-word;
    overflow-wrap: break-word;
    overflow: hidden;
    flex: 1;
    min-width: 0;
  }
`

const StyledListItem = styled(List.Item)`
  cursor: pointer;
  transition: background-color 0.2s ease;
  padding: 8px 12px;
  border-radius: 4px;

  &:hover {
    background-color: ${({ theme }) => theme.forecast.background3};
  }

  &:active {
    background-color: ${({ theme }) => theme.forecast.background2};
  }
`

const RawInferredValueItem: React.FC<{
  extractedValue: ExtractedValue
  setValue: Dispatch<SetStateAction<OntologyValueType>>
}> = ({ extractedValue, setValue }) => {
  const { value, confidence, explanation } = extractedValue
  const confidenceColor = getConfidenceColor(confidence)
  const confidencePercent = Math.round(confidence * 100)

  return (
    <StyledListItem onClick={() => setValue(value as OntologyValueType)}>
      <List.Item.Meta
        title={
          <ItemContainer>
            <span>{String(value)}</span>
            <Tooltip title={explanation}>
              <Tag color={confidenceColor} style={{ margin: 0 }}>
                {confidencePercent}%
              </Tag>
            </Tooltip>
          </ItemContainer>
        }
      />
    </StyledListItem>
  )
}
const InferredValueItem = React.memo(RawInferredValueItem)

const StyledList = styled(List<ExtractedValue>)`
  min-width: 300px;
  max-width: min(500px, 90vw);
`

const InferredValueList: React.FC<{
  extractionResults: ExtractionResult
  setValue: Dispatch<SetStateAction<OntologyValueType>>
}> = ({ extractionResults, setValue }) => {
  return (
    <StyledList
      size='small'
      dataSource={useMemo(
        () => [...extractionResults.values],
        [extractionResults.values]
      )}
      renderItem={useCallback(
        (extractedValue: ExtractedValue) => (
          <InferredValueItem
            extractedValue={extractedValue}
            setValue={setValue}
          />
        ),
        [setValue]
      )}
    />
  )
}
